import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';

/**
 * Bannière de contexte de recherche.
 * Lit automatiquement `location.state?.searchHighlight` et s'affiche
 * quand l'utilisateur arrive sur la page via un clic dans la recherche.
 *
 * Usage : monter le composant en haut de la page cible, sans props.
 * Retourne aussi `highlight` (string | null) pour que la page puisse
 * l'utiliser (pré-remplir un champ de recherche, scroller vers un élément…).
 */
export function useSearchHighlight() {
  const location = useLocation();
  const navigate = useNavigate();
  const highlight = location.state?.searchHighlight || null;

  // Efface l'état de l'historique pour éviter de le réafficher après un rechargement
  const dismiss = () => {
    navigate(location.pathname + location.search, {
      replace: true,
      state: { ...location.state, searchHighlight: undefined },
    });
  };

  return { highlight, dismiss };
}

/**
 * Composant bannière à monter en haut de page.
 * S'auto-dismiss après 10 s ou sur clic du ✕.
 */
export default function SearchHighlightBanner() {
  const { highlight, dismiss } = useSearchHighlight();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (highlight) {
      setVisible(true);
      const t = setTimeout(() => setVisible(false), 10_000);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [highlight]);

  if (!visible || !highlight) return null;

  return (
    <div className="flex items-center gap-2 px-3.5 py-2.5 bg-accent/10 border border-accent/25 rounded-xl text-sm animate-fade-in">
      <Search size={14} className="text-accent flex-shrink-0" />
      <span className="text-blue-gray-700">
        Résultat pour{' '}
        <strong className="text-accent font-semibold">« {highlight} »</strong>
      </span>
      <button
        onClick={() => { setVisible(false); dismiss(); }}
        className="ml-auto text-blue-gray-400 hover:text-blue-gray-600 transition p-0.5"
        aria-label="Fermer"
      >
        <X size={13} />
      </button>
    </div>
  );
}
